import { useAppDispatch, useAppSelector } from "@/core/app/hooks"
import {
  getCaseById,
  getCaseByPatient,
  updateCaseById,
} from "@/core/app/slices/case/caseThunkApi"
import {
  forcelockCaseApi,
  lockCaseApi,
  unlockCaseApi,
  uploadCaseZips,
} from "@/core/app/slices/clinical/clinicalThunkApi"
import {
  checkIfNeedConversion,
  closeCaseAndClearAllStatus,
  fetchZipsAndLoadCase,
} from "@/core/app/slices/clinical/clinicalworkflow"
import store, { RootState } from "@/core/app/store"
import {
  caseManagement,
  resourcesSynchronization,
  stagingManager,
  treatView,
  viewControlInTreatment,
  viewEdit,
  wasmModule,
} from "@/gluelayer"
import { Box, ThemeProvider } from "@mui/material"
import { useEffect, useRef, useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { useParams } from "react-router-dom"

import { setPhotoTypeJson } from "@/core/app/slices/records/photograph/photographSlice"
import {
  resetPrescriptionSchema,
  resetRxRefinementFormAdd,
  resetRxRefinementFormEdit,
  resetSchema,
} from "@/core/app/slices/uassist/formSlice"
import { fetchClinicalSettings } from "@/core/app/slices/user/userApis"
import { refinementModule } from "@/gluelayer/core/modules/refinement"
import { useCallbackPrompt } from "@/hooks/useCallbackPrompt"
import { useMyNavigation } from "@/hooks/useMyNavigation"
import CaseLockPopup from "@/modules/Clinical/components/CaseLockPopup/caseLockPopup"
import AssignPopup from "@/modules/Clinical/components/CloseCase/assignPopup"
import MessagePopup from "@/modules/Clinical/components/CloseCase/messagePopup"
import NoPopup from "@/modules/Clinical/components/CloseCase/noPopup"
import OrderPopup from "@/modules/Clinical/components/CloseCase/orderPopup"
import { ErrorDialogBox } from "@/modules/Clinical/components/DialogBox/errorDialogBox"
import { ScreeshotReport } from "@/modules/Clinical/components/ReportPDF/screenshotReport"
import ToothHover from "@/modules/Clinical/components/ToothHover/toothhover"
import { CreateRefinement } from "@/UDTreat/CreateRefinement"
import { CreateSelfplanRefinement } from "@/UDTreat/CreateSelfplanRefinement"
import WasmCanvas from "@/wasm3d/WasmCanvas"
import { userInitiatedOktaSignOut } from "@/core/config/okta.config"
import { useOktaAuth } from "@okta/okta-react"
import { UDBanner } from "./Banner/UDBanner"
import { BottomToolBar } from "./bottom/BottomToolBar"
import SCCallbackManager from "./bottom/StageBar/stageCommon"
import {
  caseLockByCRB,
  caseLockByOtherUser,
  caseLockByShipped,
  caseLockByUassist,
  caseUnderProduction,
} from "./bottom/Toggle/column"
import ConversionMask from "./components/ConversionMask"
import { ReopenCaseDlg } from "./components/ReopenCaseDlg"
import SmartRx from "./components/SmartRx"
import { LeftPages } from "./left/LeftPages"
import { LeftToolBar } from "./left/LeftToolBar"
import { RightPages } from "./right/RightPages"
import { RightToolBar } from "./right/RightToolBar"
import { darkTheme, lightTheme } from "./theme/UDThemes"
import { ThemeSwitch } from "./top/ThemeSwitch"
import { TopToolBar } from "./top/TopToolBar"
import AttachmentWarning from "./UDesign/Attachments/AttachmentWarning"
import BiteRampButtons from "./UDesign/Attachments/BiteRampButtons"
import { UDLockCaseForEdit } from "./UDLockCaseForEdit"
import { UDOpenCase } from "./UDOpenCasev2"
import ExtractedToothList from "./UDToothInfo/ExtractedToothList"
import UDToothInfo from "./UDToothInfo/UDToothInfo"
import {
  setCurLeftPage,
  setIsCreateNewRefinement,
  setIsCreateSelfplanRefinement,
  setIsDoingPresetup,
  setOpenBiteCheck,
  setOpenCollisionCheck,
  setOpenGrid,
  setOpenInitialFinal,
  setPlanList,
  setShowJsPreview,
  setSmartRxShow,
  setStageShowType,
  setStartAutoModelCheck,
  setSwitchViewEdit,
  setUassistRxShow,
} from "./udTreatSlice"
import { KFEditor } from "./WeDesign/KFEditor"
import { UDRetainerRxWorkflow } from "./workflow/retainer/UDRetainerWorkflow"
import { UDSmartRxWorkflow } from "./workflow/smartrx/UDSmartRxWorkflow"
// FE - print import
import Preview from "@/components/Preview/preview2"
import { safeCloseWindow } from "@/core/app/CloseTabContext"
import { fetchDoctorsList } from "@/core/app/slices/doctors/doctorThunkApi"
import {
  requestCloudPresetup,
  requestRefinePresetup,
} from "@/core/app/slices/v2/v2apis"
import { getAWSRUMClient } from "@/core/global/awsRumClient"
import { flushSync } from "react-dom"
import AIProcessTips from "./bottom/AIProcessTips"
import UDBiteReg from "./bottom/AutoModelCheck/UDBiteReg"
import useLatest from "./bottom/StageBar/useLatest"
import ReuploadForSelfPlan from "./components/ReuploadForSelfPlan"
import { OrderSplit } from "./Order/OrderSplit/OrderSplit"
import { LabelWindow } from "./Order/OrderStlPrint/SmallWindow/LabelWindow"
import { TrimWindow } from "./Order/OrderStlPrint/SmallWindow/TrimWindow"
import { UnderCutWindow } from "./Order/OrderStlPrint/SmallWindow/UnderCutWindow"
import { TopOrderPrint } from "./Order/TopComponent/TopOrderPrint"
import { TopOrderSplit } from "./Order/TopComponent/TopOrderSplit"
import { UDPonticDomeSelectMenu } from "./PonticDome/UDPonticDomeSelectMenu"
import UDModelCheck from "./right/UDModelCheck"
import ClinicalHeader from "./top/ClinicalHeader"
// import Dome from "@/modules/Clinical/components/DomePopup/dome"
import { UDStaticDomeButton } from "./PonticDome/UDStaticDomeButton"
// Record the action that triggered the lock-case (closing the case, toggling EDIT, clicking ORDER)

/**
 * record save-case event
 * @param payload
 */
const recordSaveCaseEvent = (payload) => {
  getAWSRUMClient()?.recordEvent("clinical_savecase_event", {
    ...payload,
  })
}

let lockPopupTakeControlCallback = () => {}

let clickWho = ""
export const UDTreat = () => {
  const dispatch = useAppDispatch()
  const {
    curCaseMode,
    workflowMode,
    isCasePreview,
    showBottomStatusBar,
    planList,
    reportData,
    switchViewEdit,
    refinementNum,
    isCreateNewRefinement,
    isCreateSelfplanRefinement,
    unlockAndLogout,
    openOrderSplit,
    openStlPrint,
    openLabelPreview,
    openUndercutPreview,
    openTrimPreview,
    openCollisionCheck,
    openBiteCheck,
    startAutoModelCheck,
    openBiteReg,
    isOpenModelCheck,
    isDoingPresetup,
    showJsPreview,
    notes,
  } = useAppSelector((state: RootState) => state.udTreatService)
  const { isWasmOpencaseReady, openMode } = useAppSelector(
    (state: RootState) => state.clinicalService,
  )
  const navigate = useMyNavigation()
  const [isSubmitModification, setisSubmitModification] = useState(true)
  const [isSubmitReply, setisSubmitReply] = useState(true)
  const { caseDetail } = useAppSelector((state: RootState) => state.caseService)
  const { RunQualityCheckAfterPreSetup } = useAppSelector(
    (state: RootState) => state.userService.clinicalSettings,
  )
  const { txplanSubname, txplanName, caseDisposition } = caseDetail
  const { caseId, patientId } = useParams()
  const [openCase, setOpenCase] = useState(true)
  const [darkMode, setDarkMode] = useState(false)
  const [openWarning, setOpenWarning] = useState(true)
  const pageRef = useRef(null)
  const { oktaAuth } = useOktaAuth()
  const logOutRedirect = async () => userInitiatedOktaSignOut(oktaAuth)
  const refinementNumLatest = useLatest(refinementNum)
  const [presetupErrorType, setPresetupErrorType] = useState("")
  const [isShowPresetupError, setIsShowPresetupError] = useState(false)
  // const [isCancelRefine, setIsCancelRefine] = useState(false)
  const { current_orgId } = useAppSelector(
    (state: RootState) => state.userService.user,
  )

  const [upperRefineStl, setUpperRefineStl] = useState({
    data: null,
    action: "init",
  })
  const [lowerRefineStl, setLowerRefineStl] = useState({
    data: null,
    action: "init",
  })

  const previewRef = useRef(null)
  const { workflowStatus } = useAppSelector(
    (state: RootState) => state.udTreatService,
  )

  // console.log('????>>>>>!!!!!!',workflowStatus,isDoingPresetup,)

  const initClinicalAndLoadCase = async () => {
    //logAws(ELogTypeAws.Info, "clinical_udtreat_load_123", {message: "UDTreat start..."})
    dispatch(getCaseByPatient({ patientId }))
    // dispatch(getCaseById({ patientId, caseId }))
    // clear rx data
    dispatch(resetSchema())
    dispatch(resetPrescriptionSchema())
    dispatch(resetRxRefinementFormEdit())
    dispatch(resetRxRefinementFormAdd())

    // clear photos json
    dispatch(setPhotoTypeJson(null))
    dispatch(fetchDoctorsList())

    // open case
    await dispatch(checkIfNeedConversion({ caseId, patientId }))
    await dispatch(fetchZipsAndLoadCase({ caseId, patientId }))

    if (store.getState().clinicalService.openMode === "setup") {
      // open bitecheck and collisioncheck by default
      dispatch(setOpenBiteCheck(true))
      dispatch(setOpenCollisionCheck(true))

      // 从UD点击uassist链接进入fullcase的时候，自动切换到edit模式，并打开uassist面板
      // 检查 hash 是否以 #uassit 结尾
      if (location.hash === "#uassist") {
        handleSwitchEdit()
      }
    }
    dispatch(fetchClinicalSettings())
  }

  useEffect(() => {
    initClinicalAndLoadCase()

    return () => {
      dispatch(closeCaseAndClearAllStatus(caseId))
    }
  }, [])

  // 添加新的 useEffect 用于设置键盘事件监听器
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // if (event.ctrlKey || event.metaKey) {
      if (event.ctrlKey) {
        console.log("handleKeyDown")
        const inRefinement = wasmModule.statusController.IsInRefinementModule()
        if (inRefinement) return
        switch (event.key.toLowerCase()) {
          case "z":
          case "y":
            event.preventDefault()
            console.log("undo/redo key pressed")
            if (!wasmModule.isFinishedOpencase) return
            const module = wasmModule.ulabwinIns.getModuleManager()
            const undoRedoModule = module.GetUndoOrRedoModule()
            if (undoRedoModule) {
              if (event.key.toLowerCase() === "z") {
                console.log("Executing undo")
                undoRedoModule.GetLastState()
              } else {
                console.log("Executing redo")
                undoRedoModule.GetNextState()
              }
            }
            break

          // // test auto model check
          // case "m":
          //   dispatch(setStartAutoModelCheck(true))
          //   dispatch(setStageShowType(""))
          //   dispatch(setCurLeftPage(""))
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown)

    // 清理函数
    return () => {
      document.removeEventListener("keydown", handleKeyDown)

      //unregiste the callback for uploadAllData
      SCCallbackManager.getInstance().setUploadAllDataCallBack(null)
    }
  }, []) // 空依赖数组意味着这个效果只在组件挂载和卸载时运行

  // 添加调试用快捷键
  if (import.meta.env.VITE_NODE_ENV !== "production") {
    // 打印虚拟目录文件列表
    useHotkeys("ctrl+l", async () => {
      console.log("Fetching filelist for FS_BASEPATH...")
      try {
        // 调用getFileListWithFullPath方法获取FS_BASEPATH路径下的文件列表
        const fileList =
          await resourcesSynchronization.getFileListWithFullPath()
        console.log("filelist for FS_BASEPATH：", fileList)
      } catch (error) {
        console.error("get filelist for FS_BASEPATH failed:", error)
      }
    })
  }

  const requestPresetupAlready = useRef<boolean>(false)
  const requestAiPresetup = async (
    hasUpper,
    hasLower,
    udesign_json,
    force = false,
  ) => {
    await dispatch(
      requestCloudPresetup({
        orgId: current_orgId,
        upper: hasUpper,
        lower: hasLower,
        patientId,
        caseId,
        force,
      }),
    ).then(async (res) => {
      switch (res.payload) {
        case true:
          dispatch(getCaseById({ caseId, patientId })).then(async () => {
            // clear the case cache
            // await dispatch(clearCache({ caseId }))
            // await dispatch(resetCase())
            // await dispatch(setShowBottomStatusBar(false))
            await dispatch(closeCaseAndClearAllStatus(caseId))
            await dispatch(fetchZipsAndLoadCase({ caseId, patientId }))
            dispatch(setIsDoingPresetup(false))
          })
          break
        case "failed":
          setPresetupErrorType("failed")
          setIsShowPresetupError(true)
          break
        case "retry":
          setPresetupErrorType("retry")
          setIsShowPresetupError(true)
          break
        case "network error":
        case "disconnect":
          setPresetupErrorType("network error")
          setIsShowPresetupError(true)
          break
        case "cancelfailed":
          break
        case "cancelsuccess":
          break
      }
    })
  }

  const isCreateNewRefinementLatest = useRef(isCreateSelfplanRefinement)
  isCreateNewRefinementLatest.current = isCreateSelfplanRefinement
  useEffect(() => {
    const requestPresetup = async (udesign_json) => {
      let hasUpper = true,
        hasLower = true
      hasUpper = udesign_json.upperUploaded
      hasLower = udesign_json.lowerUploaded
      requestAiPresetup(hasUpper, hasLower, udesign_json)
    }

    if (caseDetail && caseDetail.udesign_json) {
      const udesign_json = JSON.parse(caseDetail.udesign_json)

      if (
        caseDetail.extra_info?.presetup === "failed" &&
        udesign_json.needPresetup
      ) {
        setPresetupErrorType("failed")
        setIsShowPresetupError(true)
        dispatch(setIsDoingPresetup(true))
      } else if (
        caseDetail.extra_info?.presetup === "failed" &&
        udesign_json.isDoingRefinePresetup
      ) {
        flushSync(() => dispatch(setShowJsPreview(true)))
        const mtcNames = [
          "/test/case/Setting Data10/refinement/arch_u.mtc",
          "/test/case/Setting Data10/refinement/arch_l.mtc",
        ]
        const mtcFiles = resourcesSynchronization.getFilesByPathname([
          mtcNames[0],
          mtcNames[1],
        ])
        setUpperRefineStl({
          data: mtcFiles.get("arch_u.mtc") ? mtcFiles.get("arch_u.mtc") : null,
          action: "stl",
        })
        setLowerRefineStl({
          data: mtcFiles.get("arch_l.mtc") ? mtcFiles.get("arch_l.mtc") : null,
          action: "stl",
        })
        dispatch(setIsDoingPresetup(true))
        dispatch(setStageShowType(""))
        setPresetupErrorType("refinefailed")
        setIsShowPresetupError(true)
      } else if (
        caseDetail.extra_info?.presetup === "inprogress" &&
        !requestPresetupAlready.current &&
        udesign_json.needPresetup
      ) {
        requestPresetupAlready.current = true
        requestPresetup(udesign_json)
        dispatch(setIsDoingPresetup(true))
      } else if (
        caseDetail.extra_info?.presetup === "completed" &&
        udesign_json.needPresetup &&
        wasmModule.isFinishedOpencase
      ) {
        // switch to true
        lockFunc(() => {
          dispatch(setSwitchViewEdit(true))
          if (RunQualityCheckAfterPreSetup) {
            dispatch(setStageShowType(""))
            dispatch(setStartAutoModelCheck(true))
          } else {
            // open the bitecheck and collision check by default
            dispatch(setOpenBiteCheck(true))
            dispatch(setOpenCollisionCheck(true))
          }
        })

        dispatch(setCurLeftPage(""))
        dispatch(setIsDoingPresetup(false))
        const newUdesignJsonObj = {
          ...udesign_json,
          needPresetup: false,
        }
        dispatch(
          updateCaseById({
            patientId,
            caseId,
            payload: {
              case_extra_attrs: JSON.stringify(newUdesignJsonObj),
            },
          }),
        ).then(() => {
          dispatch(getCaseById({ caseId, patientId })).then(() => {})
        })
      } else if (
        caseDetail.extra_info?.presetup === "inprogress" &&
        udesign_json.isDoingRefinePresetup &&
        !requestPresetupAlready.current &&
        wasmModule.isFinishedOpencase
      ) {
        flushSync(() => dispatch(setShowJsPreview(true)))
        const mtcNames = [
          "/test/case/Setting Data10/refinement/arch_u.mtc",
          "/test/case/Setting Data10/refinement/arch_l.mtc",
        ]
        const mtcFiles = resourcesSynchronization.getFilesByPathname([
          mtcNames[0],
          mtcNames[1],
        ])
        setUpperRefineStl({
          data: mtcFiles.get("arch_u.mtc") ? mtcFiles.get("arch_u.mtc") : null,
          action: "stl",
        })
        setLowerRefineStl({
          data: mtcFiles.get("arch_l.mtc") ? mtcFiles.get("arch_l.mtc") : null,
          action: "stl",
        })
        dispatch(setIsDoingPresetup(true))
        dispatch(setStageShowType(""))
        requestRefinementPresetup(
          mtcFiles.get("arch_u.mtc") ? true : false,
          mtcFiles.get("arch_l.mtc") ? true : false,
          udesign_json,
          false,
        )
        requestPresetupAlready.current = true
      } else if (
        caseDetail.extra_info?.presetup === "completed" &&
        udesign_json.isDoingRefinePresetup &&
        wasmModule.isFinishedOpencase
      ) {
        lockFunc(() => {
          dispatch(setSwitchViewEdit(true))
          viewEdit.switchViewToEdit(true)
          if (RunQualityCheckAfterPreSetup) {
            dispatch(setStageShowType(""))
            dispatch(setStartAutoModelCheck(true))
          } else {
            // open the bitecheck and collision check by default
            dispatch(setOpenBiteCheck(true))
            dispatch(setOpenCollisionCheck(true))
          }
          const newUdesignJsonObj = {
            ...udesign_json,
            isDoingRefinePresetup: false,
          }
          dispatch(
            updateCaseById({
              patientId,
              caseId,
              payload: {
                case_extra_attrs: JSON.stringify(newUdesignJsonObj),
              },
            }),
          )
        })
      }
    }
  }, [caseDetail, wasmModule.isFinishedOpencase])

  useEffect(() => {
    console.info("treat planList: ", planList)
    //registe the callback for uploadAllData
    SCCallbackManager.getInstance().setUploadAllDataCallBack(uploadAllData)
  }, [planList])

  // Assembly plan data
  const assemblyPlanData = (planData: string[]) => {
    const txArr = []
    planData.map((i, j) => {
      txArr.push({
        txName: i.trim(),
        index: j + 1,
      })
    })
    return txArr
  }
  // function to deletePlan
  const deletePlanFunction = (plan = currentPlan.index) => {
    const isDel = caseManagement.delTx(plan)
    // if the plan is deleted, then update the case files
    caseManagement.setTxDataChanged(true)
    const txList = caseManagement.getTreatmentPlanList()
    const txArr = assemblyPlanData(txList)
    setCurrentPlan(txArr[0])
    setPlanList(txArr)
    return isDel
  }

  const [isShowAssign, setIsShowAssign] = useState(false)
  // order states
  const [isClickOrder, setisClickOrder] = useState(false)
  // case lock popup
  const [openLockPopup, setOpenLockPopup] = useState<boolean>(false)
  // case lock type
  //-1--- CRB  ,0---lock by uassist,   1---order been place, 0.5---order been place close case ,  2----edit by other user  ,
  const [lockType, setLockType] = useState<number>(2)
  const [allowUnlock, setAllowUnlock] = useState(true)
  const [ispopupAfterOrder, setispopupAfterOrder] = useState(false)

  // lock by username and machine
  const [lockInfo, setLockInfo] = useState<{
    username: string
    device_info: string
    locked_date: string
  }>({ username: "", device_info: "", locked_date: "" })

  /**
   * lock popup confirm override function
   */
  const lockPopupConfirm = () => {
    // click lock control or override button
    const clickControlOverride = (
      isForceModify = false,
      overrideCallBak?: () => void,
    ) => {
      dispatch(forcelockCaseApi({ patientId, caseId, isForceModify })).then(
        ({ payload }) => {
          const { status } = payload
          console.log("????2222222,00000", payload)
          if (status === "Success") {
            dispatch(setSwitchViewEdit(true))
            setOpenLockPopup(false)
            dispatch(getCaseById({ patientId, caseId })).then((res) => {
              console.log("getCaseById res: ", res)
            })
            overrideCallBak && overrideCallBak()
            // continue the next action
            lockPopupTakeControlCallback && lockPopupTakeControlCallback()
          } else {
            // Force unlock failed
            handleLockError(
              payload,
              overrideCallBak, //callback
              true, // allowControl
            )
          }
        },
      )
    }

    if (lockType === 3) {
      clickControlOverride(false, () => {
        uploadAllData({
          callback: () => {
            dispatch(unlockCaseApi({ patientId, caseId })).then(async () => {
              await dispatch(setSwitchViewEdit(false))
              setOpenLockPopup(false)
              safeCloseWindow()
            })
          },
        })
      })
    } else {
      const isForceModify = lockType == 4 // for in production case, set force_modify=true
      clickControlOverride(isForceModify)
    }
    const urlParams = new URLSearchParams(window.location.search)
    const doRefinement = urlParams.get("doRefinement")
    if (doRefinement == "true") {
      treatView.startSplitScreenToCreateRefinement()
      dispatch(setIsCreateNewRefinement(true))
      dispatch(setStageShowType("compare"))
    }
  }

  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(
    switchViewEdit || isClickOrder || !isSubmitReply,
  )
  // current tx
  const [currentPlan, setCurrentPlan] = useState<{
    txName: string
    index: 2 | 1
  }>({})

  //max refine
  const [maxRefine, setMaxRefine] = useState<number>(0)
  // pdf shoot
  const reportPdfRef = useRef(null)
  const openOrUploadReportPdf = (status: "open" | "upload") => {
    reportPdfRef?.current.uploadReportPDF(status)
  }
  // lock case function
  // unlock request
  const unlockRequest = () => {
    dispatch(unlockCaseApi({ patientId, caseId })).then(({ payload }) => {
      const { status } = payload
      if (status === "Success") {
        dispatch(setSwitchViewEdit(false))
        if (unlockAndLogout) logOutRedirect()
      } else {
        const { errors } = payload
        const { error_message } = errors[0]
        alert(error_message)
      }
    })
  }
  // unlock post request
  const unlockFunc = () => {
    uploadAllData({ callback: unlockRequest })
  }
  // lock post request
  const lockFunc = (callBack?: () => void, allowControl = true) => {
    dispatch(lockCaseApi({ patientId, caseId })).then(async ({ payload }) => {
      const { status } = payload
      if (status === "Success") {
        await dispatch(getCaseById({ patientId, caseId }))
        callBack && callBack()
      } else {
        handleLockError(payload, callBack, allowControl)
      }
      clickWho = ""
    })
  }

  const handleLockError = (lockErrorPayload, callBack, allowControl) => {
    const { errors, username, device_info, locked_date } = lockErrorPayload.data
    const { error_message } = errors[0]
    const info = error_message.split("::")[0]
    if (caseLockByUassist.includes(info)) {
      setLockType(0)
      setAllowUnlock(allowControl)
      setOpenLockPopup(true)
    } else if (caseLockByShipped.includes(info)) {
      if (clickWho === "close" && switchViewEdit) {
        setLockType(0.5)
        clickWho = ""
      } else {
        setLockType(1)
      }
      setAllowUnlock(allowControl)
      setOpenLockPopup(true)
    } else if (caseLockByOtherUser.includes(info)) {
      setLockInfo({ username, device_info, locked_date })
      const lockType = switchViewEdit
        ? clickWho === "close" || clickWho === "order"
          ? 3
          : 2
        : 2
      console.log("lock case payload:", lockType, clickWho)
      setLockType(lockType)
      if (lockType === 2) {
        lockPopupTakeControlCallback = callBack
      }
      setAllowUnlock(allowControl)
      setOpenLockPopup(true)
    } else if (caseLockByCRB.includes(info)) {
      setLockType(-1)
      setAllowUnlock(allowControl)
      setOpenLockPopup(true)
    } else if (caseUnderProduction == info) {
      setLockType(4)
      setAllowUnlock(true)
      setOpenLockPopup(true)
    }
  }

  // switch edit or view function
  const handleSwitchEdit = () => {
    clickWho = "switch"
    if (!switchViewEdit) {
      // lock case
      lockFunc(() => {
        dispatch(setSwitchViewEdit(true))
        const urlParams = new URLSearchParams(window.location.search)
        const doRefinement = urlParams.get("doRefinement")

        if (doRefinement == "true") {
          treatView.startSplitScreenToCreateRefinement()
          dispatch(setIsCreateNewRefinement(true))
          dispatch(setStageShowType("compare"))
        }
      })
    } else {
      // unlock case
      unlockFunc()
    }
  }

  const handleLockRequestFromToolbar = (
    callBack,
    clickWho: "close" | "order" | "print",
  ) => {
    clickWho = clickWho ?? "order"
    lockFunc(() => {
      dispatch(setSwitchViewEdit(true))
      callBack()
    }, true)
  }
  /**
   * update the case detail by the case id
   * @param txname:the active case
   * @param other: the other payload for the case detail api
   */
  const updateCaseFun = ({ txname = planList[0].txName, ...other }) => {
    let extralInfo = {}
    // extralInfo.UpperRetainer = 1
    // extralInfo.LowerRetainer = 1
    let newUdesignJsonObj = {}
    let isHasAttachmentData
    let isHasIPRData
    try {
      extralInfo = caseManagement.getCaseExtraInfomation()

      isHasAttachmentData =
        caseManagement.getAttachmentIPRData()?.isHasAttachmentData
      isHasIPRData = caseManagement.getIsHasIPRNode()
    } catch {
      return
    }
    const isStartFromOne = refinementModule.getIsStartFromOne()
    dispatch(getCaseById({ patientId, caseId })).then((res) => {
      if (res?.payload?.udesign_json) {
        const udesign_json = JSON.parse(res.payload.udesign_json)
        //Merge with existing JSON.
        newUdesignJsonObj = {
          ...udesign_json,
          ...extralInfo,
        }
      } else {
        newUdesignJsonObj = { ...extralInfo }
      }
      const txsubname = maxRefine
        ? `Course Refinement ${maxRefine}`
        : planList[0].txName !== txname
        ? planList[0].txName
        : planList[1]?.txName || ""

      const case_extra_info = {
        case_extra_attrs: JSON.stringify(newUdesignJsonObj),
        txplan_name: txname,
        txplan_subname: txsubname,
        course_check_number: isStartFromOne
          ? refinementNumLatest.current - 1
          : 0,
        complexity: isHasAttachmentData || isHasIPRData ? 2 : 1,
        planned_upper:
          Number(
            stagingManager.wasmStageData.jointUpKeypoints[
              stagingManager.wasmStageData.jointUpKeypoints.length - 1
            ]?.name,
          ) || 0,
        planned_lower:
          Number(
            stagingManager.wasmStageData.jointLowerKeypoints[
              stagingManager.wasmStageData.jointLowerKeypoints.length - 1
            ]?.name,
          ) || 0,
        ...other,
      }
      dispatch(
        updateCaseById({
          patientId,
          caseId,
          payload: case_extra_info,
        }),
      )
      recordSaveCaseEvent({
        ...case_extra_info,
      })
    })
  }
  /**
   * upload all of the datas when close the case
   * @param txname:active plan in the case(modifi plan or assign plan or order plan)
   * @param txsubname:inactive plan in the case
   * @param callback:the function after upload zips
   * @param other: the payload for upload case detail
   */

  const uploadAllData = ({
    txname = txplanName,
    txsubname = txplanSubname,
    callback = () => {
      return
    },
    ...other
  }: {
    txname?: string
    txsubname?: string
    callback: () => void
    other?: any
  }) => {
    if (!txname) {
      // if there is no txname in the DB
      txname = planList[0]?.txName
    }
    if (!txsubname && planList[1]) {
      // if there is no txsubname in the DB
      txsubname = planList[1]?.txName
    }
    // if there is no plan
    if (!txname) {
      callback && callback()
      return
    }
    // if case been uploaded will not upload agagin
    const isTxDataChanged = caseManagement.getTxDataChanged() //方案是否有修改
    const isPrintChanged =
      !!wasmModule.stagingcontoler?.GetPrintPreviewChanged() // print change bool
    const doneActionTypeStr = wasmModule.statusController?.GetDoneActionType()
    const hasDoneAction = !!doneActionTypeStr
    if (!isTxDataChanged && !isPrintChanged && !hasDoneAction) {
      callback && callback()
      return
    }

    lockFunc(async () => {
      const isTxDataChanged = caseManagement.getTxDataChanged() //方案是否有修改
      if (isTxDataChanged) {
        caseManagement.saveCurrentTx(true)
        caseManagement.setTxDataChanged(false)
      }
      updateCaseFun({ txname, txsubname, ...other })

      if (!isCasePreview) {
        // update pdf
        openOrUploadReportPdf("upload")
      }

      let uploadFolders: any = ["bulk2"]
      console.log("GetDoneActionType: ", doneActionTypeStr)
      if (doneActionTypeStr !== "") {
        const jsonObject = JSON.parse(doneActionTypeStr)
        for (let key in jsonObject) {
          if (!uploadFolders.includes("bulk1")) {
            if (
              key === "Resegment" ||
              key === "Relabel" ||
              key === "Space Correction"
            ) {
              uploadFolders.push("bulk1")
            }
          }
          if (!uploadFolders.includes("bulk10")) {
            if (key === "Resegment") {
              uploadFolders.push("bulk10")
            }
          }
        }
      }

      // update zips
      caseManagement.getCaseFiles(uploadFolders).then((r) => {
        const zips = []
        if (r) {
          for (const k in r) {
            zips.push({
              fileName: k,
              file: r[k],
            })
          }

          dispatch(
            uploadCaseZips({
              zips,
              patientId,
              caseId,
            }),
          ).then(({ payload }) => {
            if (zips.length === payload.length) {
              for (let i = 0; i < payload.length; i++) {
                if (payload[i].status !== 200) {
                  return
                }
              }
              // caseManagement.closeCase()
              callback && callback()
            }
          })
        }
      })
    })
  }

  useEffect(() => {
    // clear the lock popup callback when close the popup
    if (!openLockPopup) {
      lockPopupTakeControlCallback = () => {}
    }
  }, [openLockPopup])

  useEffect(() => {
    if (unlockAndLogout) unlockFunc()
  }, [unlockAndLogout])

  // 传递给3d view or edit
  useEffect(() => {
    if (!wasmModule.isFinishedOpencase) return
    viewEdit.switchViewToEdit(switchViewEdit)
  }, [switchViewEdit])

  // // auto switch to edit after open full case
  // useEffect(() => {
  //   if (wasmModule.isFinishedOpencase) {
  //     if (
  //       // if the doctor open it in other end. it will be locked.
  //       CASE_STATUS.ATTENTION_NEEDED !== caseDisposition &&
  //       caseDisposition !== ""
  //     ) {
  //       // lock case
  //       lockFunc(() => {
  //         dispatch(setSwitchViewEdit(true))
  //         viewEdit.switchViewToEdit(true)
  //       })
  //     }
  //   }
  // }, [wasmModule.isFinishedOpencase])

  useEffect(() => {
    console.info("treat currentPlan: ", currentPlan.txName, currentPlan.index)
  }, [currentPlan])

  const onclickCloseBtn = (callBack) => {
    clickWho = "close"
    if (!isCreateNewRefinement) {
      setIsShowAssign(true)
    } else {
      setIsShowAssign(false)
      callBack()
    }
  }

  // left smartRx
  const { smartRxShow, uassistRxShow } = useAppSelector(
    (state: RootState) => state.udTreatService,
  )
  const gotoUPrintScene = () => {
    viewControlInTreatment.zoomWithValueInTreatment(1.0)
    console.log("wasmModule.moduleManager: ", wasmModule.moduleManager)
    const vecObj = new wasmModule.module.IntVector()
    vecObj.push_back(30) //  dont close  30: E_UDesignModuleToothEruption
    vecObj.push_back(32) //  32: E_UDesignModuleRefinementDisplay
    //we must close splitScreen before close ala Modules
    if (wasmModule.moduleManager.IsSplitScreenOpened()) {
      wasmModule.statusController.SplitScreenDisplay(false)
    }
    if (openCollisionCheck) {
      dispatch(setOpenCollisionCheck(false))
      viewControlInTreatment.setSpacecheckVisibility(false)
    }
    if (openBiteCheck) {
      dispatch(setOpenBiteCheck(false))
      viewControlInTreatment.setOcclusionVisibility(false)
    }

    wasmModule.moduleManager.CloseAllModules(vecObj)
    wasmModule.moduleManager.SwitchModulePrintSelection(true)
    let canvas = document.getElementById("canvas")
    if (canvas) {
      canvas.tabIndex = 0
      canvas.focus()
    }
    console.log("canvas focus: ", canvas)
  }

  const getPrintComponent = () => {
    useEffect(() => {
      if (openStlPrint) {
        gotoUPrintScene()
      }
    }, [openStlPrint])

    return (
      <>
        {openStlPrint && <TopOrderPrint />}
        {openOrderSplit && !openStlPrint && <TopOrderSplit />}
        {openOrderSplit && <OrderSplit setisClickOrder={setisClickOrder} />}
        {openUndercutPreview && openStlPrint && <UnderCutWindow />}
        {openLabelPreview && openStlPrint && <LabelWindow />}
        {openTrimPreview && openStlPrint && <TrimWindow />}
      </>
    )
  }
  // postmessage from other page to open refinement
  const [refinementType, setRefinementType] = useState<string>("")
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return
      if (event.data.type) {
        setRefinementType(event.data.type)
      }
      if (window.opener) {
        window.opener.postMessage({ type: "success" }, window.location.origin)
      }
      window.removeEventListener("message", handleMessage)
    }
    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [])

  // open uassit refinement screen
  const asyncShowRefinementPage = async (isSelfplan = false) => {
    await dispatch(setOpenInitialFinal(false))
    flushSync(() => setOpenGrid(false))
    treatView.startSplitScreenToCreateRefinement()
    dispatch(setIsCreateNewRefinement(true))
    if (isSelfplan) {
      dispatch(setIsCreateSelfplanRefinement(true))
    }
    dispatch(setStageShowType("compare"))
  }
  // auto open refinement
  useEffect(() => {
    if (!wasmModule.isFinishedOpencase) return
    if (refinementType === "uassistRefinement") {
      asyncShowRefinementPage().then(() => {
        setRefinementType("")
      })
    } else if (refinementType === "selfRefinement") {
      // selfRefinement
      asyncShowRefinementPage(true)
    }
  }, [refinementType, wasmModule.isFinishedOpencase])

  // Move the logic for automatically opening uassist-message from LeftToolBar to UDTreat to prevent duplicate execution.
  useEffect(() => {
    if (
      caseDetail.caseDisposition === "ATTENTION_NEEDED" ||
      caseDetail.caseDisposition === "REVIEW_PLAN" ||
      caseDetail.caseDisposition === "REVIEW_MODIFICATION"
    ) {
      dispatch(setCurLeftPage("uassist"))
    }
  }, [caseDetail.caseDisposition])

  const doShowJsPreview = () => {
    flushSync(() => dispatch(setShowJsPreview(true)))
    const mtcNames = [
      "/test/case/Setting Data10/refinement/arch_u.mtc",
      "/test/case/Setting Data10/refinement/arch_l.mtc",
    ]
    const mtcFiles = resourcesSynchronization.getFilesByPathname([
      mtcNames[0],
      mtcNames[1],
    ])
    setUpperRefineStl({
      data: mtcFiles.get("arch_u.mtc") ? mtcFiles.get("arch_u.mtc") : null,
      action: "stl",
    })
    setLowerRefineStl({
      data: mtcFiles.get("arch_l.mtc") ? mtcFiles.get("arch_l.mtc") : null,
      action: "stl",
    })
  }

  const requestRefinementPresetup = async (
    hasUpper,
    hasLower,
    udesign_json,
    force = true,
  ) => {
    await dispatch(
      requestRefinePresetup({
        orgId: current_orgId,
        upper: hasUpper,
        lower: hasLower,
        patientId,
        caseId,
        force,
      }),
    ).then(async (res) => {
      if (res.payload) {
        switch (res.payload) {
          case true:
            // await dispatch(clearCache({ caseId }))
            // await dispatch(resetCase())
            // dispatch(setShowBottomStatusBar(false))
            await dispatch(closeCaseAndClearAllStatus(caseId))
            await dispatch(fetchZipsAndLoadCase({ caseId, patientId }))
            dispatch(getCaseById({ caseId, patientId }))
            dispatch(setCurLeftPage(""))
            dispatch(setIsDoingPresetup(false))
            dispatch(setShowJsPreview(false))
            break
          case "cancelfailed":
            caseManagement
              .getCaseFiles(["bulk0", "bulk1", "bulk2", "bulk10"])
              .then((r) => {
                const zips = []
                if (r) {
                  for (const k in r) {
                    zips.push({
                      fileName: k,
                      file: r[k],
                    })
                  }
                  dispatch(
                    uploadCaseZips({
                      zips,
                      patientId,
                      caseId,
                    }),
                  )
                }
              })
            break
          case "failed":
            setPresetupErrorType("refinefailed")
            setIsShowPresetupError(true)
            break
          case "retry":
            setPresetupErrorType("refineretry")
            setIsShowPresetupError(true)
            break
          case "network error":
          case "disconnect":
            setPresetupErrorType("refine network error")
            setIsShowPresetupError(true)
            break
        }
      }
    })
  }

  return (
    <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
      <Box
        ref={pageRef}
        sx={{
          display: "flex",
          top: 60,
          left: 0,
          bottom: 0,
          width: "100%",
          flexDirection: "column",
          border: "none",
          backgroundColor:
            isDoingPresetup && isWasmOpencaseReady ? "rgb(160,163,165)" : "",
        }}
      >
        <Box sx={{ flexGrow: 1, overflow: "hidden" }} position="relative">
          {isDoingPresetup && isWasmOpencaseReady && <AIProcessTips />}
          <WasmCanvas />

          {showJsPreview && (
            <Box
              // id="3dcontainer"
              ref={previewRef}
              sx={{
                backgroundColor: "#333",
                position: "relative",
                width: "100%",
                height: "100%",
              }}
            >
              {
                <Preview
                  upperStl={upperRefineStl}
                  lowerStl={lowerRefineStl}
                  canvasRef={previewRef.current}
                  sx={{ top: 20 }}
                />
              }
            </Box>
          )}

          <ClinicalHeader onclickCloseBtn={onclickCloseBtn} />
          {/* start ****各种状态下展示不同的topbar */}
          {!openOrderSplit && (
            <TopToolBar
              isShowAssign={isShowAssign}
              setLockType={setLockType}
              setOpenLockPopup={setOpenLockPopup}
              setisClickOrder={setisClickOrder}
              setispopupAfterOrder={setispopupAfterOrder}
              switchEditCallBack={() => {
                handleSwitchEdit()
              }}
              isEdit={switchViewEdit}
              ispopup={isClickOrder || !isSubmitReply}
              requestLock={handleLockRequestFromToolbar}
              onclickCloseBtn={onclickCloseBtn}
            />
          )}
          {getPrintComponent()}
          {/* {openStlPrint && <TopOrderPrint />}
          {openOrderSplit && !openStlPrint && <TopOrderSplit />}
          {openOrderSplit && <OrderSplit setisClickOrder={setisClickOrder} />}
          {openUndercutPreview && openStlPrint && <UnderCutWindow />}
          {openLabelPreview && openStlPrint && <LabelWindow />}
          {openTrimPreview && openStlPrint && <TrimWindow />} */}
          {/* ****end****** */}

          <BottomToolBar
            parentRef={pageRef}
            setTreatCurrentPlan={setCurrentPlan}
            uploadAllData={uploadAllData}
            lockFunc={lockFunc}
          />
          <ExtractedToothList />
          <ThemeSwitch
            sx={{
              position: "absolute",
              right: "180px",
              top: "-45px",
            }}
            value={darkMode}
            onChange={(e) => {
              setDarkMode(e.target.checked)
            }}
          />
          {workflowMode === "aidesign" &&
            !openOrderSplit &&
            !startAutoModelCheck &&
            !isOpenModelCheck && <LeftToolBar />}
          {!openOrderSplit && (
            <LeftPages
              isEdit={switchViewEdit}
              setisSubmitModification={setisSubmitModification}
              setisSubmitReply={setisSubmitReply}
              isSubmitReply={isSubmitReply}
              isSubmitModification={isSubmitModification}
              showPrompt={showPrompt}
              confirmNavigation={confirmNavigation}
              cancelNavigation={cancelNavigation}
              uploadAllData={uploadAllData}
            />
          )}
          <UDToothInfo isOpenCase={openCase} isEdit={switchViewEdit} />
          <UDPonticDomeSelectMenu />
          {/* <Dome/> */}
          <UDStaticDomeButton />
          {openCase && <ToothHover />}
          {openBiteReg && <UDBiteReg />}

          {workflowStatus === "clinical" && <RightToolBar />}
          <RightPages />
          {switchViewEdit &&
            workflowStatus === "clinical" &&
            !isDoingPresetup && <UDModelCheck />}
          <BiteRampButtons />
          <SmartRx
            needToSubmit={false}
            smartRxShow={smartRxShow}
            setSmartRxShow={setSmartRxShow}
            styleObj={{ left: 84 }}
          />
          <SmartRx
            unlockFunc={unlockFunc}
            needToSubmit={true}
            smartRxShow={uassistRxShow}
            setSmartRxShow={setUassistRxShow}
            styleObj={{ right: 84 }}
          />
          <UDOpenCase />
          <ConversionMask />
          <UDLockCaseForEdit />
          <UDBanner
            open={openWarning}
            onOK={() => {
              setOpenWarning(false)
            }}
          />
          <ReuploadForSelfPlan lockFunc={lockFunc} />
          {workflowMode === "smartrx" && <UDSmartRxWorkflow />}
          {workflowMode === "retainerrx" && <UDRetainerRxWorkflow />}
          {/* {showBottomStatusBar && <BottomStatusBar />} */}
          {/* <BottomStatusBar /> */}
          {/* curCaseMode === "WeDesign" && <WeStageBar /> */}
          {curCaseMode === "WeDesign" && <KFEditor />}
          <ReopenCaseDlg />
          {isCreateNewRefinement && !isCreateSelfplanRefinement && (
            <CreateRefinement
              onClose={() => {
                // setOpenRefDialog(false)
                treatView.Open(false)
                dispatch(setStageShowType("normal"))
                dispatch(setIsCreateNewRefinement(false))
              }}
            ></CreateRefinement>
          )}
          {isCreateNewRefinement && isCreateSelfplanRefinement && (
            <CreateSelfplanRefinement
              requestRefinementPresetup={requestRefinementPresetup}
              // setIsCancelRefine={setIsCancelRefine}
              showJsPreview={doShowJsPreview}
            ></CreateSelfplanRefinement>
          )}
        </Box>
        <AttachmentWarning switchViewEdit={switchViewEdit} />
        {/* {!isCasePreview && <BottomToolBar />} */}
        {/* close no popup */}
        <NoPopup
          showPrompt={showPrompt && isSubmitModification && isSubmitReply}
          confirmNavigation={confirmNavigation}
          isActive={(isClickOrder && !ispopupAfterOrder) || !isClickOrder}
          uploadAllData={uploadAllData}
          isClickOrder={isClickOrder}
        />
        {/* case lock popup */}
        <CaseLockPopup
          lockType={lockType}
          openLockPopup={openLockPopup}
          setOpenLockPopup={setOpenLockPopup}
          lockInfo={lockInfo}
          clickControlOverride={lockPopupConfirm}
          allowControl={allowUnlock}
          cancelNavigation={cancelNavigation}
        />
        {/* close case popup */}
        <AssignPopup
          currentPlan={currentPlan}
          planList={planList}
          showPrompt={isShowAssign}
          // setCloseClick={setCloseClick}
          confirmNavigation={confirmNavigation}
          isActive={!isClickOrder}
          uploadReportPdf={() => openOrUploadReportPdf("upload")}
          uploadAllData={uploadAllData}
          deletePlanFunction={deletePlanFunction}
          isEdit={switchViewEdit}
          setIsShowAssign={setIsShowAssign}
        />
        {/* if 2 plan popup*/}
        <OrderPopup
          currentPlan={currentPlan}
          confirmNavigation={confirmNavigation}
          cancelNavigation={cancelNavigation}
          isClickOrder={
            isClickOrder && ispopupAfterOrder
            //&& isSubmitModification
          }
          setisClickOrder={setisClickOrder}
          setispopupAfterOrder={setispopupAfterOrder}
          planList={planList}
          uploadReportPdf={() => openOrUploadReportPdf("upload")}
          deletePlanFunction={deletePlanFunction}
          lockFunc={lockFunc}
          uploadAllData={uploadAllData}
        ></OrderPopup>
        {/** Fill in the full report pdf */}
        <ScreeshotReport
          // isReady={preparation}
          isReady={true}
          attachment={reportData.attachment}
          toothlist={reportData.toothlist}
          iprData={reportData.iprData}
          ref={reportPdfRef}
          notes={notes}
        />
        <MessagePopup
          cancelNavigation={cancelNavigation}
          showPrompt={
            showPrompt &&
            ((isClickOrder
              ? !isSubmitModification && planList.length === 1
              : !isSubmitModification) ||
              !isSubmitReply)
          }
          setisSubmitModification={setisSubmitModification}
          setisSubmitReply={setisSubmitReply}
          isSubmitModification={isSubmitModification}
          uploadAllData={uploadAllData}
          confirmNavigation={confirmNavigation}
          isEdit={switchViewEdit}
        ></MessagePopup>
        <ErrorDialogBox
          isShow={isShowPresetupError}
          setIsShowPresetupError={setIsShowPresetupError}
          errorType={presetupErrorType}
          requestAiPresetup={() => {
            const udesign_json = JSON.parse(caseDetail.udesign_json)
            if (udesign_json.needPresetup) {
              let hasUpper = true,
                hasLower = true
              hasUpper = udesign_json.upperUploaded
              hasLower = udesign_json.lowerUploaded
              requestAiPresetup(hasUpper, hasLower, udesign_json, true)
            } else if (udesign_json.isDoingRefinePresetup) {
              const mtcNames = [
                "/test/case/Setting Data10/refinement/arch_u.mtc",
                "/test/case/Setting Data10/refinement/arch_l.mtc",
              ]
              const mtcFiles = resourcesSynchronization.getFilesByPathname([
                mtcNames[0],
                mtcNames[1],
              ])
              requestRefinementPresetup(
                mtcFiles.get("arch_u.mtc") ? true : false,
                mtcFiles.get("arch_l.mtc") ? true : false,
                udesign_json,
                true,
              )
            }
          }}
        ></ErrorDialogBox>
      </Box>
    </ThemeProvider>
  )
}
